import React, { useState, useEffect, useMemo } from 'react';
import { db, collection, getDocs, updateDoc, doc, setDoc, addDoc, deleteDoc, onSnapshot, writeBatch, serverTimestamp } from '../firebase';
import { useTeam } from '../contexts/TeamContext';
import { useIsTeamOwner, useCanEditTeam } from '../hooks/useIsTeamOwner';
import { ensureOwnerMembership, migrateLegacyPlayerToMember, normalizeEmail, memberIdFromEmail } from '../utils/membership';
import '../App.css';

/* eslint-disable jsx-a11y/accessible-emoji */

const looksLikeEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());

const AdminPanel = ({ user, view = 'all' }) => {
  // `view` controls which sub-sections render:
  //   'all'     — original behavior (players + season)
  //   'players' — only player management
  //   'season'  — only season tools (archive/reset)
  const showPlayers = view === 'all' || view === 'players';
  const showSeason = view === 'all' || view === 'season';
  const showHeader = view === 'all';
  const { teamId, teamData, loading: teamLoading } = useTeam();
  const isTeamOwner = useIsTeamOwner(user);   // za auto-membership effects (owner-only)
  const canEditTeam = useCanEditTeam(user);   // za pristup panelu (owner ili manager)
  const [players, setPlayers] = useState([]);     // legacy: teams/{id}/players
  const [members, setMembers] = useState([]);     // new model: teams/{id}/members

  // PlayerManager state
  const [addError, setAddError] = useState('');
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [editingPlayerName, setEditingPlayerName] = useState('');

  // All players from root collection (loaded once, filtered client-side)
  const [allGlobalPlayers, setAllGlobalPlayers] = useState([]);

  // Search existing users (Tok A)
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [addingSelected, setAddingSelected] = useState(false);

  // Invite new player modal (Tok B — shadow member)
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');

  // Add player by name only (no email) — legacy players doc
  const [showAddNameModal, setShowAddNameModal] = useState(false);
  const [newPlayerNameOnly, setNewPlayerNameOnly] = useState('');
  const [addingNameOnly, setAddingNameOnly] = useState(false);
  const [addNameOnlyError, setAddNameOnlyError] = useState('');

  // Add email naknadno na legacy igrača (bez emaila)
  const [addEmailForEntryId, setAddEmailForEntryId] = useState(null);
  const [addEmailValue, setAddEmailValue] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);
  const [addEmailError, setAddEmailError] = useState('');

  const ownerEmail = teamData?.ownerEmail || '';
  const userEmail = user?.email || '';
  const userDisplayName = user?.displayName || '';

  useEffect(() => {
    if (!teamId) return;
    const unsubPlayers = onSnapshot(
      collection(db, "teams", teamId, "players"),
      (snapshot) => {
        const playersList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setPlayers(playersList);
      },
      (error) => {
        console.error("Greška pri dohvaćanju igrača (legacy):", error);
      }
    );
    const unsubMembers = onSnapshot(
      collection(db, "teams", teamId, "members"),
      (snapshot) => {
        const all = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setMembers(all.filter(m => !m.removed));
        // removed members → available for re-adding via "Prikaži sve"
        const removed = all.filter(m => m.removed).map(m => ({
          name: m.displayName || m.email,
          email: normalizeEmail(m.email),
        }));
        setAllGlobalPlayers(prev => {
          const globalEmails = new Set(prev.map(p => p.email));
          const extras = removed.filter(r => !globalEmails.has(r.email));
          return extras.length > 0 ? [...prev, ...extras] : prev;
        });
      },
      (error) => {
        console.error("Greška pri dohvaćanju članova:", error);
      }
    );

    return () => {
      unsubPlayers();
      unsubMembers();
    };
  }, [teamId]);

  useEffect(() => {
    getDocs(collection(db, 'players'))
      .then(snap => {
        const fromDb = snap.docs.map(d => ({ ...d.data(), email: normalizeEmail(d.data().email || d.id) }));
        setAllGlobalPlayers(prev => {
          const dbEmails = new Set(fromDb.map(p => p.email));
          const extras = prev.filter(p => !dbEmails.has(p.email));
          return [...fromDb, ...extras];
        });
      })
      .catch(err => console.warn('Nije moguće učitati globalne igrače:', err));
  }, []);

  const availableGlobalPlayers = useMemo(() => {
    const existingEmails = new Set(members.map(m => normalizeEmail(m.email)));
    return allGlobalPlayers
      .filter(p => !existingEmails.has(p.email))
      .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email, 'hr'));
  }, [allGlobalPlayers, members]);

  // ---------------------------------------------------------
  // Combined roster: union members + legacy players by name.
  // Members (novi model) imaju prioritet; legacy se prikazuje
  // samo ako isto ime još nije prebačeno u members.
  // ---------------------------------------------------------
  const roster = useMemo(() => {
    const byName = new Map();
    const normalizedOwnerEmail = normalizeEmail(ownerEmail);
    const normalizedUserEmail = normalizeEmail(userEmail);
    const normalizedUserDisplayName = userDisplayName.trim().toLowerCase();
    for (const p of players) {
      if (p.migratedToMemberId) continue;
      if (
        looksLikeEmail(p.name) ||
        (normalizedOwnerEmail && normalizeEmail(p.email) === normalizedOwnerEmail)
      ) {
        continue;
      }
      const key = (p.name || '').trim().toLowerCase();
      byName.set(key, {
        kind: 'legacy',
        id: p.id,
        name: p.name || '(bez imena)',
        email: p.email || null,
        role: 'player',
        claimed: false,
        goals: p.goals || 0,
        assists: p.assists || 0,
        matchesPlayed: p.matchesPlayed || 0,
        raw: p,
      });
    }
    for (const m of members) {
      if (m.role === 'owner') continue;
      const shouldUseOwnerDisplayName =
        normalizedUserDisplayName &&
        normalizeEmail(m.email) === normalizedUserEmail &&
        normalizeEmail(m.email) === normalizedOwnerEmail &&
        (
          !m.displayName ||
          normalizeEmail(m.displayName) === normalizeEmail(m.email)
        );
      const effectiveName = shouldUseOwnerDisplayName
        ? userDisplayName
        : (m.displayName || m.email || '');
      const key = effectiveName.trim().toLowerCase();
      const legacy = byName.get(key);
      byName.set(key, {
        kind: 'member',
        id: m.id,
        name: effectiveName || '(bez imena)',
        email: m.email || null,
        role: m.role || 'player',
        claimed: !!m.claimed,
        goals: m.goals || legacy?.goals || 0,
        assists: m.assists || legacy?.assists || 0,
        matchesPlayed: m.matchesPlayed || legacy?.matchesPlayed || 0,
        raw: m,
        legacyId: legacy?.kind === 'legacy' ? legacy.id : null,
        legacyMerged: legacy?.kind === 'legacy',
      });
    }
    return Array.from(byName.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    );
  }, [members, ownerEmail, players, userDisplayName, userEmail]);

  useEffect(() => {
    if (!isTeamOwner || !teamId || !teamData?.ownerEmail || !user?.uid) return;

    const ownerMemberId = memberIdFromEmail(teamData.ownerEmail);
    if (!ownerMemberId) return;
    const existingOwnerMember = members.find((m) => m.id === ownerMemberId);
    const preferredDisplayName =
      (user.displayName || '').trim() || (teamData.name || '').trim();

    if (existingOwnerMember) {
      const shouldRenameOwner =
        preferredDisplayName &&
        existingOwnerMember.role === 'owner' &&
        existingOwnerMember.displayName !== preferredDisplayName &&
        (
          !existingOwnerMember.displayName ||
          existingOwnerMember.displayName === teamData.name ||
          normalizeEmail(existingOwnerMember.displayName) === normalizeEmail(teamData.ownerEmail)
        );

      if (!shouldRenameOwner) return;

      updateDoc(doc(db, 'teams', teamId, 'members', ownerMemberId), {
        displayName: preferredDisplayName,
      }).catch((error) => {
        console.warn('Nisam mogao ispraviti owner displayName:', error);
      });
      return;
    }

    ensureOwnerMembership(teamId, {
      uid: user.uid,
      email: teamData.ownerEmail,
      displayName: preferredDisplayName,
      createdBy: user.uid,
    }).catch((error) => {
      console.warn('Nisam mogao automatski kreirati owner membership:', error);
    });
  }, [isTeamOwner, teamId, teamData, user, members]);

  useEffect(() => {
    if (!isTeamOwner || !teamId || !teamData?.ownerEmail) return;
    const preferredDisplayName = (user?.displayName || '').trim();
    if (!preferredDisplayName) return;

    const ownerMemberId = memberIdFromEmail(teamData.ownerEmail);
    const ownerMember = members.find((m) => m.id === ownerMemberId);
    const legacyOwner = players.find(
      (p) =>
        !p.migratedToMemberId &&
        (p.name || '').trim().toLowerCase() === preferredDisplayName.toLowerCase()
    );

    if (!ownerMember || !legacyOwner) return;

    const shouldMigrate =
      ownerMember.displayName !== preferredDisplayName ||
      (ownerMember.goals || 0) !== (legacyOwner.goals || 0) ||
      (ownerMember.assists || 0) !== (legacyOwner.assists || 0) ||
      (ownerMember.matchesPlayed || 0) !== (legacyOwner.matchesPlayed || 0);

    if (!shouldMigrate) return;

    migrateLegacyPlayerToMember(teamId, {
      legacyPlayerId: legacyOwner.id,
      memberEmail: teamData.ownerEmail,
      displayName: preferredDisplayName,
    }).catch((error) => {
      console.warn('Nisam mogao migrirati legacy owner zapis u member:', error);
    });
  }, [isTeamOwner, teamId, teamData, user, members, players]);

  if (teamLoading) {
    return <div className="loading-screen">Učitavanje tima…</div>;
  }

  if (!canEditTeam) {
    return <p>Nemate ovlasti za pristup ovoj stranici.</p>;
  }

  const handleSearch = (term) => {
    setSearchTerm(term);
    setSelectedUsers(new Set());
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }
    const lower = term.trim().toLowerCase();
    const existingEmails = new Set(members.map(m => normalizeEmail(m.email)));
    const results = allGlobalPlayers.filter(p => {
      if (existingEmails.has(p.email)) return false;
      const nameMatch = (p.name || '').toLowerCase().includes(lower);
      const emailMatch = (p.email || '').toLowerCase().includes(lower);
      return nameMatch || emailMatch;
    });
    setSearchResults(results.slice(0, 10));
  };
  const toggleSelectUser = (email) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const ownerMember = members.find(
    (m) => normalizeEmail(m.email) === normalizeEmail(userEmail)
  );
  const ownerNotYetPlayer = ownerMember?.role === 'owner';

  const handleAddSelfAsPlayer = async () => {
    if (!teamId || !user?.uid || !userEmail) return;
    const memberId = memberIdFromEmail(normalizeEmail(userEmail));
    const ref = doc(db, 'teams', teamId, 'members', memberId);
    try {
      await updateDoc(ref, { role: 'player' });
    } catch (err) {
      console.error('Nije moguće dodati sebe kao igrača:', err);
    }
  };

  const handleAddSelectedUsers = async () => {
    if (!teamId || selectedUsers.size === 0) return;
    setAddingSelected(true);
    setAddError('');
    try {
      const batch = writeBatch(db);
      for (const email of selectedUsers) {
        const u = searchResults.find(r => r.email === email);
        if (!u) continue;
        const memberId = memberIdFromEmail(email);
        const memberRef = doc(db, 'teams', teamId, 'members', memberId);
        batch.set(memberRef, {
          email,
          displayName: u.name || email,
          role: 'player',
          uid: null,
          claimed: false,
          createdAt: serverTimestamp(),
          createdBy: user?.uid || null,
        });
      }
      await batch.commit();
      setSelectedUsers(new Set());
      setSearchTerm('');
      setSearchResults([]);
    } catch (err) {
      setAddError(err.message || 'Greška pri dodavanju igrača.');
    } finally {
      setAddingSelected(false);
    }
  };

    const handleInvitePlayer = async (e) => {
    e.preventDefault();
    setInviteError('');
    if (!inviteEmail.trim() || !looksLikeEmail(inviteEmail)) { setInviteError('Unesi ispravan email.'); return; }

    const normalized = normalizeEmail(inviteEmail.trim());
    const memberId = memberIdFromEmail(normalized);

    if (members.some(m => normalizeEmail(m.email) === normalized)) {
      setInviteError('Igrač s tim emailom već je u timu.');
      return;
    }

    setInviting(true);
    try {
      await setDoc(doc(db, 'teams', teamId, 'members', memberId), {
        email: normalized,
        displayName: normalized,
        uid: null,
        claimed: false,
        role: 'player',
        createdAt: serverTimestamp(),
      });
      setShowInviteModal(false);
      setInviteEmail('');
    } catch (err) {
      setInviteError(err?.message || 'Greška pri dodavanju igrača.');
    } finally {
      setInviting(false);
    }
  };

  const handleAddNameOnlyPlayer = async (e) => {
    e.preventDefault();
    setAddNameOnlyError('');
    const name = newPlayerNameOnly.trim();
    if (!name) { setAddNameOnlyError('Unesi ime igrača.'); return; }

    const lower = name.toLowerCase();
    if (roster.some(r => r.name.trim().toLowerCase() === lower)) {
      setAddNameOnlyError('Igrač s tim imenom već postoji u timu.');
      return;
    }

    setAddingNameOnly(true);
    try {
      await addDoc(collection(db, 'teams', teamId, 'players'), {
        name,
        goals: 0,
        assists: 0,
        matchesPlayed: 0,
        createdAt: serverTimestamp(),
      });
      setShowAddNameModal(false);
      setNewPlayerNameOnly('');
    } catch (err) {
      setAddNameOnlyError(err?.message || 'Greška pri dodavanju igrača.');
    } finally {
      setAddingNameOnly(false);
    }
  };

  const handleStartAddEmail = (entry) => {
    setAddEmailForEntryId(entry.id);
    setAddEmailValue('');
    setAddEmailError('');
  };

  const handleCancelAddEmail = () => {
    setAddEmailForEntryId(null);
    setAddEmailValue('');
    setAddEmailError('');
  };

  const handleSaveEmailForLegacyPlayer = async (entry) => {
    setAddEmailError('');
    if (!addEmailValue.trim() || !looksLikeEmail(addEmailValue)) {
      setAddEmailError('Unesi ispravan email.');
      return;
    }

    const normalized = normalizeEmail(addEmailValue.trim());
    if (members.some(m => normalizeEmail(m.email) === normalized)) {
      setAddEmailError('Taj email je već u timu.');
      return;
    }

    setAddingEmail(true);
    try {
      const memberId = memberIdFromEmail(normalized);
      await setDoc(doc(db, 'teams', teamId, 'members', memberId), {
        email: normalized,
        displayName: entry.name,
        uid: null,
        claimed: false,
        role: 'player',
        createdAt: serverTimestamp(),
      });
      await migrateLegacyPlayerToMember(teamId, {
        legacyPlayerId: entry.id,
        memberEmail: normalized,
        displayName: entry.name,
      });
      setAddEmailForEntryId(null);
      setAddEmailValue('');
    } catch (err) {
      setAddEmailError(err?.message || 'Greška pri dodavanju emaila.');
    } finally {
      setAddingEmail(false);
    }
  };

  const handleStartEditPlayer = (entry) => {
    // entry.id is "member:xxx" or "legacy:xxx" scoped key; we store both
    setEditingPlayerId(`${entry.kind}:${entry.id}`);
    setEditingPlayerName(entry.name || '');
  };

  const handleCancelEditPlayer = () => {
    setEditingPlayerId(null);
    setEditingPlayerName('');
  };

  const handleSavePlayerName = async () => {
    if (!teamId || !editingPlayerId) return;
    const name = editingPlayerName.trim();
    if (!name) return;

    const [kind, rawId] = editingPlayerId.split(':');
    const current = roster.find(r => r.kind === kind && r.id === rawId);
    if (!current) return;
    const oldName = current.name;

    // Spriječi duplikate (osim samog sebe) — po combined rosteru.
    const lower = name.toLowerCase();
    if (roster.some(r => !(r.kind === kind && r.id === rawId) && r.name.trim().toLowerCase() === lower)) {
      alert('Drugi igrač već ima to ime.');
      return;
    }

    try {
      if (kind === 'member') {
        const memberRef = doc(db, 'teams', teamId, 'members', rawId);
        await updateDoc(memberRef, { displayName: name });
      } else {
        const playerRef = doc(db, 'teams', teamId, 'players', rawId);
        await updateDoc(playerRef, { name });
      }

      // Ako je ime doista promijenjeno, propagiraj ga kroz utakmice
      // (teamA, teamB, stats[ime]) da statistika i povijest ostanu konzistentni.
      if (oldName && oldName !== name) {
        const matchesSnap = await getDocs(collection(db, "teams", teamId, "matches"));
        for (const m of matchesSnap.docs) {
          const data = m.data();
          const patch = {};
          let touched = false;

          if (Array.isArray(data.teamA) && data.teamA.includes(oldName)) {
            patch.teamA = data.teamA.map(n => n === oldName ? name : n);
            touched = true;
          }
          if (Array.isArray(data.teamB) && data.teamB.includes(oldName)) {
            patch.teamB = data.teamB.map(n => n === oldName ? name : n);
            touched = true;
          }
          if (data.stats && Object.prototype.hasOwnProperty.call(data.stats, oldName)) {
            const newStats = { ...data.stats };
            newStats[name] = newStats[oldName];
            delete newStats[oldName];
            patch.stats = newStats;
            touched = true;
          }

          if (touched) {
            await updateDoc(doc(db, "teams", teamId, "matches", m.id), patch);
          }
        }
      }

      setEditingPlayerId(null);
      setEditingPlayerName('');
    } catch (error) {
      console.error("Greška pri preimenovanju igrača:", error);
      alert("❌ Greška pri spremanju imena.");
    }
  };

  const handleDeletePlayer = async (entry) => {
    if (!teamId) return;
    const confirmMsg =
      `Obrisati igrača "${entry.name}"?\n\n` +
      "Ovo briše samo zapis igrača — postojeće utakmice u kojima je sudjelovao ostaju netaknute " +
      "(ime se i dalje prikazuje u povijesti). Nastaviti?";
    if (!window.confirm(confirmMsg)) return;

    try {
      if (entry.kind === 'member') {
        // Soft delete: članstva čuvamo radi povijesti i claim stanja.
        const memberRef = doc(db, 'teams', teamId, 'members', entry.id);
        await updateDoc(memberRef, { removed: true });
      } else {
        await deleteDoc(doc(db, 'teams', teamId, 'players', entry.id));
      }
    } catch (error) {
      console.error('Greška pri brisanju igrača:', error);
      alert('❌ Greška pri brisanju igrača.');
    }
  };

  const handleArchiveSeason = async () => {
    if (!teamId) return;
    try {
      // Odredi sljedeći redni broj sezone tako da nikad ne prepišemo postojeću
      // arhivu. Uzima se najveći postojeći numerički ID iz `seasons` + 1.
      // Backward-kompatibilno: ako tim već ima `seasons/1`, nova arhiva ide na 2.
      const seasonsSnap = await getDocs(collection(db, "teams", teamId, "seasons"));
      let maxExisting = 0;
      seasonsSnap.docs.forEach((sd) => {
        const n = parseInt(sd.id, 10);
        if (Number.isFinite(n) && n > maxExisting) maxExisting = n;
      });
      const nextSeason = maxExisting + 1;
      const seasonId = String(nextSeason);

      const confirmMsg =
        `Arhivirati trenutnu sezonu kao "Sezona ${nextSeason}"?\n\n` +
        `Svi trenutni igrači i utakmice bit će kopirani u arhivu (seasons/${nextSeason}). ` +
        `Trenutna sezona ostaje netaknuta — odvojeno možeš resetirati statistike kad želiš započeti novu.`;
      if (!window.confirm(confirmMsg)) return;

      const playersSnapshot = await getDocs(collection(db, "teams", teamId, "players"));
      const membersSnapshot = await getDocs(collection(db, "teams", teamId, "members"));
      const matchesSnapshot = await getDocs(collection(db, "teams", teamId, "matches"));

      // Firestore writeBatch ima limit od 500 operacija — chunkamo na 400.
      const CHUNK = 400;
      const ops = [];

      playersSnapshot.docs.forEach((playerDoc) => {
        ops.push({
          ref: doc(db, "teams", teamId, "seasons", seasonId, "players", playerDoc.id),
          data: playerDoc.data(),
        });
      });

      membersSnapshot.docs.forEach((memberDoc) => {
        ops.push({
          ref: doc(db, "teams", teamId, "seasons", seasonId, "members", memberDoc.id),
          data: memberDoc.data(),
        });
      });

      matchesSnapshot.docs.forEach((matchDoc) => {
        ops.push({
          ref: doc(db, "teams", teamId, "seasons", seasonId, "matches", matchDoc.id),
          data: matchDoc.data(),
        });
      });

      for (let i = 0; i < ops.length; i += CHUNK) {
        const batch = writeBatch(db);
        ops.slice(i, i + CHUNK).forEach(({ ref, data }) => batch.set(ref, data));
        await batch.commit();
      }

      // Označi sezonu metapodatkom (datum, broj) i posloži brojač na timu.
      await setDoc(
        doc(db, "teams", teamId, "seasons", seasonId),
        {
          number: nextSeason,
          archivedAt: new Date().toISOString(),
          playersCount: playersSnapshot.size,
          membersCount: membersSnapshot.size,
          matchesCount: matchesSnapshot.size,
        },
        { merge: true }
      );

      try {
        await updateDoc(doc(db, "teams", teamId), {
          archivedSeasonsCount: nextSeason,
          currentSeason: nextSeason + 1,
        });
      } catch (metaErr) {
        // Ne želimo da pad meta-update-a sruši uspješnu arhivu.
        console.warn("Arhiva spremljena, ali brojač sezone na timu nije ažuriran:", metaErr);
      }

      alert(`✅ Sezona ${nextSeason} je uspješno arhivirana!`);
    } catch (error) {
      console.error("❌ Greška pri arhiviranju sezone:", error);
      alert("❌ Greška pri arhiviranju. Pogledaj konzolu.");
    }
  };

  const handleResetSeason = async () => {
    if (!teamId) return;
    if (!window.confirm("Jeste li sigurni da želite resetirati sezonu?")) return;
    try {
      const playersSnapshot = await getDocs(collection(db, "teams", teamId, "players"));
      const matchesSnapshot = await getDocs(collection(db, "teams", teamId, "matches"));

      for (const playerDoc of playersSnapshot.docs) {
        const playerRef = doc(db, "teams", teamId, "players", playerDoc.id);
        await updateDoc(playerRef, {
          goals: 0,
          assists: 0,
          matchesPlayed: 0,
          stats: {},
        });
      }

      for (const matchDoc of matchesSnapshot.docs) {
        const matchRef = doc(db, "teams", teamId, "matches", matchDoc.id);
        await deleteDoc(matchRef);
      }

      alert("✅ Resetiranje sezone je uspješno završeno!");
    } catch (error) {
      console.error("❌ Greška pri resetiranju sezone:", error);
      alert("❌ Greška pri resetiranju. Pogledaj konzolu.");
    }
  };

  const resetAllStats = async () => {
    if (!teamId) return;
    if (!window.confirm("Jeste li sigurni da želite resetirati sve statistike igrača na nulu?")) return;

    try {
      for (const player of players) {
        const playerRef = doc(db, "teams", teamId, "players", player.id);
        await updateDoc(playerRef, {
          goals: 0,
          assists: 0,
          matchesPlayed: 0
        });
      }
      for (const m of members) {
        const memberRef = doc(db, 'teams', teamId, 'members', m.id);
        await updateDoc(memberRef, {
          goals: 0,
          assists: 0,
          matchesPlayed: 0,
          stats: {},
        });
      }
      alert("Sve statistike igrača su resetirane!");
      window.location.reload();
    } catch (error) {
      console.error("Greška pri resetiranju statistika igrača:", error);
    }
  };

  return (
    <div className="admin-container">
      {showHeader && (
        <>
          <div className="section-header" style={{ marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>👥 Igrači i statistika</h3>
            <span className="section-badge">{roster.length} igrača</span>
          </div>
          <p style={{ color: 'var(--text-3)', fontSize: '0.88rem', marginBottom: 20 }}>
            Dodaj, preimenuj i obriši igrače tima. Statistika se automatski računa iz utakmica, a ispod su alati za upravljanje sezonom.
          </p>
        </>
      )}

      {showPlayers && (
      <div className="admin-section">
        <h3>👥 Igrači tima</h3>
        <p style={{ color: 'var(--text-3)', fontSize: '0.88rem', marginBottom: 14 }}>
          Pretraži registrirane korisnike po imenu ili emailu, ili pozovi novog igrača koji se još nije registrirao.
        </p>

        {/* Search + invite row */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'center' }}>
          <input
            type="text"
            className="input-field"
            placeholder="Pretraži po imenu ili emailu…"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => { setShowAddNameModal(true); setAddNameOnlyError(''); }}
          >
            + Dodaj igrača (samo ime)
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => { setShowInviteModal(true); setInviteError(''); }}
          >
            + Pozovi novog igrača
          </button>
        </div>
        <div style={{ marginBottom: 12 }}>
          <button
            type="button"
            className="btn btn-sm"
            style={{ fontSize: '0.82rem', color: 'var(--text-3)', background: 'none', border: 'none', padding: '2px 0', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => {
              if (searchTerm === '' && searchResults.length > 0) {
                setSearchResults([]);
                setSelectedUsers(new Set());
              } else {
                setSearchResults(availableGlobalPlayers);
                setSearchTerm('');
              }
            }}
          >
            Prikaži sve igrače ({availableGlobalPlayers.length})
          </button>
        </div>

        {searchTerm.length >= 2 && searchResults.length === 0 && (
          <p style={{ color: 'var(--text-3)', fontSize: '0.85rem', marginBottom: 8 }}>Nema rezultata.</p>
        )}
        {searchResults.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 10px 0' }}>
              {searchResults.map(u => (
                <li key={u.email} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <input
                    type="checkbox"
                    checked={selectedUsers.has(u.email)}
                    onChange={() => toggleSelectUser(u.email)}
                    id={`player-${u.email}`}
                  />
                  <label htmlFor={`player-${u.email}`} style={{ cursor: 'pointer', flex: 1 }}>
                    <div><strong>{u.name || u.email}</strong></div>
                    {u.name && <div style={{ color: 'var(--text-3)', fontSize: '0.82rem' }}>{u.email}</div>}
                  </label>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={selectedUsers.size === 0 || addingSelected}
              onClick={handleAddSelectedUsers}
            >
              {addingSelected ? '⏳ Dodajem…' : `Dodaj odabrane (${selectedUsers.size})`}
            </button>
          </div>
        )}

        {addError && (
          <p className="error-message" style={{ marginTop: 10 }}>{addError}</p>
        )}

        {/* Invite modal */}
        {showInviteModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
              <h3 style={{ margin: '0 0 4px' }}>Pozovi novog igrača</h3>
              <p style={{ color: 'var(--text-3)', fontSize: '0.85rem', marginBottom: 16 }}>
                Igrač će se pojaviti u timu. Kad se registrira s ovim emailom, automatski se poveže s timom.
              </p>
              {inviteError && <p className="error-message" style={{ marginBottom: 12 }}>⚠ {inviteError}</p>}
              <form onSubmit={handleInvitePlayer}>
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    className="input-field"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder="marko@example.com"
                    autoFocus
                  />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                  <button type="submit" className="btn btn-primary" disabled={inviting} style={{ flex: 1 }}>
                    {inviting ? 'Dodajem…' : 'Dodaj u tim'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowInviteModal(false); setInviteError(''); }} disabled={inviting}>
                    Odustani
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add name-only modal */}
        {showAddNameModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
              <h3 style={{ margin: '0 0 4px' }}>Dodaj igrača (samo ime)</h3>
              <p style={{ color: 'var(--text-3)', fontSize: '0.85rem', marginBottom: 16 }}>
                Igrač se dodaje bez emaila i ne može se prijaviti u aplikaciju. Email mu možeš naknadno dodati klikom na "+ Dodaj email" pored njegovog imena.
              </p>
              {addNameOnlyError && <p className="error-message" style={{ marginBottom: 12 }}>⚠ {addNameOnlyError}</p>}
              <form onSubmit={handleAddNameOnlyPlayer}>
                <div className="form-field">
                  <label>Ime i prezime</label>
                  <input
                    type="text"
                    className="input-field"
                    value={newPlayerNameOnly}
                    onChange={e => setNewPlayerNameOnly(e.target.value)}
                    placeholder="Marko Marić"
                    autoFocus
                  />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                  <button type="submit" className="btn btn-primary" disabled={addingNameOnly} style={{ flex: 1 }}>
                    {addingNameOnly ? 'Dodajem…' : 'Dodaj igrača'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowAddNameModal(false); setAddNameOnlyError(''); }} disabled={addingNameOnly}>
                    Odustani
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isTeamOwner && ownerNotYetPlayer && (
          <div style={{ marginBottom: 14 }}>
            <button className="btn btn-secondary btn-sm" onClick={handleAddSelfAsPlayer}>
              + Dodaj sebe kao igrača
            </button>
          </div>
        )}

        {roster.length === 0 ? (
          <div className="empty-inline" style={{ marginTop: 14 }}>
            Još nema igrača u ovom timu.
          </div>
        ) : (
          <ul className="player-list">
            {roster.map((player) => (
                <li key={`${player.kind}:${player.id}`} className="player-item player-item-editable">
                  {editingPlayerId === `${player.kind}:${player.id}` ? (
                    <div className="player-edit-row">
                      <input
                        type="text"
                        className="input-field"
                        value={editingPlayerName}
                        onChange={(e) => setEditingPlayerName(e.target.value)}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSavePlayerName();
                          if (e.key === 'Escape') handleCancelEditPlayer();
                        }}
                      />
                      <button
                        onClick={handleSavePlayerName}
                        className="btn btn-primary btn-sm"
                        disabled={!editingPlayerName.trim()}
                      >
                        💾 Spremi
                      </button>
                      <button
                        onClick={handleCancelEditPlayer}
                        className="btn btn-secondary btn-sm"
                      >
                        Odustani
                      </button>
                    </div>
                  ) : addEmailForEntryId === player.id && player.kind === 'legacy' ? (
                    <div className="player-edit-row">
                      <input
                        type="email"
                        className="input-field"
                        value={addEmailValue}
                        onChange={(e) => setAddEmailValue(e.target.value)}
                        placeholder="marko@example.com"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEmailForLegacyPlayer(player);
                          if (e.key === 'Escape') handleCancelAddEmail();
                        }}
                      />
                      <button
                        onClick={() => handleSaveEmailForLegacyPlayer(player)}
                        className="btn btn-primary btn-sm"
                        disabled={!addEmailValue.trim() || addingEmail}
                      >
                        {addingEmail ? '⏳' : '💾 Spremi'}
                      </button>
                      <button
                        onClick={handleCancelAddEmail}
                        className="btn btn-secondary btn-sm"
                        disabled={addingEmail}
                      >
                        Odustani
                      </button>
                      {addEmailError && (
                        <p className="error-message" style={{ width: '100%', margin: '4px 0 0' }}>⚠ {addEmailError}</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="player-name-stats">
                        <div>
                          <strong>{player.name || '(bez imena)'}</strong>
                          {player.kind === 'legacy' && !player.email && (
                            <span style={{ marginLeft: 8, fontSize: '0.78rem', color: 'var(--text-3)' }}>bez emaila</span>
                          )}
                        </div>
                        <span className="player-stat-pills">
                          <span>⚽ {player.goals || 0}</span>
                          <span>🎯 {player.assists || 0}</span>
                          <span>🏆 {player.matchesPlayed || 0}</span>
                        </span>
                      </div>
                      <div className="player-actions">
                        {player.kind === 'legacy' && !player.email && (
                          <button
                            onClick={() => handleStartAddEmail(player)}
                            className="btn btn-secondary btn-sm"
                          >
                            + Dodaj email
                          </button>
                        )}
                        <button
                          onClick={() => handleStartEditPlayer(player)}
                          className="btn btn-info btn-sm"
                        >
                          ✏️ Preimenuj
                        </button>
                        <button
                          onClick={() => handleDeletePlayer(player)}
                          className="btn btn-danger btn-sm"
                        >
                          🗑️ Obriši
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
          </ul>
        )}
      </div>
      )}

      {showSeason && (
      <div className="admin-section">
        <h3>🗓️ Sezona (napredno)</h3>
        <p style={{ color: 'var(--text-3)', fontSize: '0.85rem', marginBottom: 12 }}>
          Arhiviraj kopira trenutne igrače i utakmice u arhivu (seasons/N) — svaka nova arhiva
          dobiva sljedeći broj, postojeće se ne prepisuju. Resetiranje briše utakmice i postavlja
          statistiku igrača na 0. Pažljivo.
        </p>
        <div className="admin-actions-row">
          <button onClick={handleArchiveSeason} className="archive-button">📦 Arhiviraj sezonu</button>
          <button onClick={handleResetSeason} className="btn btn-danger">🔄 Resetiraj sezonu</button>
          <button onClick={resetAllStats} className="btn btn-danger">🗑 Resetiraj statistike igrača</button>
        </div>
      </div>
      )}

    </div>
  );
};

/* eslint-enable jsx-a11y/accessible-emoji */

export default AdminPanel;
